import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CopyFormStage } from './SessionsCopyDialog.types';
import { TextFieldProps, Typography } from '@material-ui/core';
import { CancelButton, NextButton, useStyles } from './SessionsCopyDialog.styles';
import { CopyFormHeader } from './CopyFormHeader.component';
import { BasicDataForm } from './BasicDataForm.component';
import useDebounce from '../../../hooks/useDebounce';
import { verifyDuplicateExamSessionName } from '../../../services/validation/validation.service';
import { useLayoutContext } from '../../../Context/Layout.context';
import { getSessionsFromSystemOne, startCopyingProcess } from '../../../services/exams/sessions/sessions.service';
import { SessionCopyBackendCode } from '../../../components/ExamResultsRepository/ExamResultsRepository.types';
import { ExamSessionSimpleDto } from '../../../models/examSessionDto.types';
import { SheetDefinitionForm } from './SheetDefinitionForm.component';
import { Prompt } from '../../../components/Prompt/Prompt.component';

interface SessionsCopyDialogContentProps {
  sheetId: string;
  onClose: () => void;
}

const maxYear = 9999;
const minMonth = 1;
const maxMonth = 12;

export const SessionsCopyDialogContent: React.FC<SessionsCopyDialogContentProps> = ({
  sheetId,
  onClose,
}: SessionsCopyDialogContentProps) => {
  const { openAlert } = useLayoutContext();
  const classes = useStyles();

  const [currentStage, setCurrentStage] = useState<CopyFormStage>(CopyFormStage.BasicData);
  const [useExistingSession, setUseExistingSession] = useState<boolean>(false);
  const textInputRef = useRef<TextFieldProps>();
  const [name, setName] = useState<string>('');
  const [validName, setValidName] = useState<boolean>(true);
  const [isTrainingSession, setIsTrainingSession] = useState<boolean>(false);
  const [selectedSession, setSelectedSession] = useState<ExamSessionSimpleDto | null>(null);
  const [availableSessions, setAvailableSessions] = useState<ExamSessionSimpleDto[]>([]);
  const [examSheetName, setExamSheetName] = useState<string>('');
  const [errorElement, setErrorElement] = useState<JSX.Element | null>(null);
  const [code, setCode] = useState<string>('');
  const debouncedInputName = useDebounce(name, 500);

  const [type, setType] = useState<string>('');
  const [year, setYear] = useState<number>(2000);
  const [month, setMonth] = useState<number>(1);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.id) {
      case 'type':
        setType(event.target.value.trimStart());
        break;
      case 'year':
        if (event.target.value.trimStart().length) {
          const providedYear = Number(event.target.value.trimStart());
          if (providedYear > maxYear || providedYear < 0) setYear(new Date().getFullYear());
          else setYear(providedYear);
        }
        break;
      case 'month':
        const providedMonth = Number(event.target.value.trimStart());
        if (providedMonth > maxMonth || providedMonth < minMonth) setMonth(1);
        else setMonth(providedMonth);
        break;
      case 'name':
        setName(event.target.value.trim());
        break;
      case 'examSheetName':
        setExamSheetName(event.target.value.trimStart());
        break;
      case 'code':
        setCode(event.target.value.trimStart());
        break;
    }
  };

  const loadSessions = useCallback(
    (signal: AbortSignal) => {
      if (useExistingSession) {
        getSessionsFromSystemOne(signal)
          .then((res) => {
            setAvailableSessions(res);
          })
          .catch(() => {
            openAlert('get_error');
          });
      }
    },
    [useExistingSession],
  );

  useEffect(() => {
    const abort = new AbortController();
    loadSessions(abort.signal);

    return () => {
      abort.abort();
    };
  }, [useExistingSession]);

  useEffect(() => {
    if (debouncedInputName) {
      verifyDuplicateExamSessionName(debouncedInputName).then(
        (result) => {
          setValidName(result);
        },
        () => openAlert('error'),
      );
    } else {
      setValidName(true);
    }
  }, [debouncedInputName]);

  const checkNextDisabled = () => {
    if (useExistingSession && selectedSession !== null) return false;

    if (name === '' || !validName) return true;
    if (type === '') return true;

    return false;
  };

  const handleStartCopyProcess = () => {
    startCopyingProcess(sheetId, {
      examCode: code,
      examName: examSheetName,
      examSessionName: name,
      examSessionId: selectedSession ? selectedSession?.id : null,
      examSessionType: type,
      isTraining: selectedSession ? null : isTrainingSession,
      month: selectedSession ? null : month,
      year: selectedSession ? null : year,
    })
      .then((res) => {
        if (res.copyExamSheetResponse === SessionCopyBackendCode.Success) {
          openAlert('save_success');
          onClose();
          return;
        }
        if (res.copyExamSheetResponse === SessionCopyBackendCode.AnotherCopyInProgress) {
          setErrorElement(
            <>
              <Typography variant="body1" className={classes.validationErrorText}>
                {'Nie możesz rozpocząć kopiowania arkusza, ponieważ aktualnie trwa kopiowanie innego arkusza: '}
                <span className={classes.validationErrorTextBold}>{res.sheetCode}</span>
              </Typography>
            </>,
          );
          return;
        }
        openAlert('save_error');
      })
      .catch((res) => {
        console.error('err', res);
        openAlert('save_error');
      });
  };

  const handleSelectedSession = (id: string | null) => {
    if (!id || id === '') {
      setSelectedSession(null);
      return;
    }
    const found = availableSessions.find((x) => x.id === id);
    if (found) {
      setSelectedSession(found);
    }
  };

  const isNextEnabled = checkNextDisabled();
  const isSaveEnabled = currentStage === CopyFormStage.SheetDefinition && (code === '' || examSheetName === '');

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ width: '100%', height: 'calc(100% - 48px)', overflowY: 'auto' }}>
        <CopyFormHeader currentStage={currentStage} />
        {currentStage === CopyFormStage.BasicData && (
          <BasicDataForm
            textInputRef={textInputRef}
            name={name}
            validName={validName}
            isTrainingSession={isTrainingSession}
            handleIsTrainingSession={(val) => setIsTrainingSession(val)}
            type={type}
            year={year}
            month={month}
            handleChange={handleChange}
            useExistingSession={useExistingSession}
            handleUseExistingSessionChange={() => setUseExistingSession((prev) => !prev)}
            availableSessions={availableSessions}
            selectedSession={selectedSession}
            setSelectedSession={handleSelectedSession}
          />
        )}
        {currentStage === CopyFormStage.SheetDefinition && (
          <SheetDefinitionForm examSheetName={examSheetName} code={code} handleChange={handleChange} />
        )}
      </div>
      <div style={{ padding: '0px 12px' }}>
        <div style={{ width: '100%', display: 'inline-flex', justifyContent: 'flex-end', gap: 8 }}>
          <CancelButton onClick={() => onClose()}>Anuluj</CancelButton>
          {currentStage === CopyFormStage.BasicData && (
            <NextButton onClick={() => setCurrentStage(CopyFormStage.SheetDefinition)} disabled={isNextEnabled}>
              Dalej
            </NextButton>
          )}
          {currentStage === CopyFormStage.SheetDefinition && (
            <NextButton onClick={() => handleStartCopyProcess()} disabled={isSaveEnabled}>
              Zapisz
            </NextButton>
          )}
        </div>
      </div>
      <Prompt
        type="error"
        open={errorElement !== null}
        enhancedText={errorElement ? errorElement : <></>}
        mainActionButtonText="OK"
        onConfirm={() => {
          setErrorElement(null);
        }}
      />
    </div>
  );
};
